import { ListOrderedIcon } from '@primer/octicons-react';
import { GraphQLError } from 'graphql/index';
import React, { Suspense } from 'react';
import { graphql, useMutation } from 'react-relay';

import Button from '../../components/core/Button';
import ErrorBoundary from '../../components/core/ErrorBoundary';
import FormLayout from '../../components/core/FormLayout';
import Formik from '../../components/core/Formik';
import Grid from '../../components/core/Grid';
import Head from '../../components/core/Head';
import { HeaderSidebarNavPageLayout } from '../../components/core/Layout';
import NonFieldError from '../../components/core/NonFieldError';
import NumberField from '../../components/core/NumberField';
import Spinner from '../../components/core/Spinner';
import Text from '../../components/core/Text';
import View from '../../components/core/View';
import useLazyLoadQuery from '../../hooks/useLazyLoadQuery';
import useToast from '../../hooks/useToast';
import { productPeriodOrder_productPeriodOrdersQuery } from '../../relay/__generated__/productPeriodOrder_productPeriodOrdersQuery.graphql';
import { productPeriodOrder_productPeriodOrdersUpdateMutation } from '../../relay/__generated__/productPeriodOrder_productPeriodOrdersUpdateMutation.graphql';
import { parseGraphQLError } from '../../utils/error';
import { isNullable } from '../../utils/is';
import { NextPage } from '../_app';

const productPeriodOrdersForProductPeriodOrder = graphql`
  query productPeriodOrder_productPeriodOrdersQuery {
    productPeriodOrders {
      periodText
      ordering
    }
  }
`;

type FormValues = { orders: { periodText: string; ordering: number | null }[] };

// 판매 유형 순서: 칸마다 1 이상 정수 하나, 같은 숫자 금지 (서버도 같은 규칙으로 거부한다)
const validateProductPeriodOrders = (orders: readonly { ordering?: number | null }[]): (string | undefined)[] => {
  const isValidOrdering = (ordering: number) => Number.isInteger(ordering) && ordering >= 1;

  return orders.map(({ ordering }, index) => {
    if (isNullable(ordering) || Number.isNaN(ordering)) return '순서를 입력해 주세요';
    if (!isValidOrdering(ordering)) return '1 이상의 정수로 입력해 주세요';

    const isDuplicated = orders.some((order, otherIndex) => otherIndex !== index && order.ordering === ordering);
    if (isDuplicated) return '다른 유형과 같은 순서예요. 다른 숫자를 입력해 주세요';

    return undefined;
  });
};

const ProductPeriodOrderForm = () => {
  const [{ productPeriodOrders }, refresh] = useLazyLoadQuery<productPeriodOrder_productPeriodOrdersQuery>(
    productPeriodOrdersForProductPeriodOrder,
    {},
    { fetchPolicy: 'store-and-network' },
  );
  const { toast } = useToast();

  const [mutate] = useMutation<productPeriodOrder_productPeriodOrdersUpdateMutation>(graphql`
    mutation productPeriodOrder_productPeriodOrdersUpdateMutation($orders: [ProductPeriodOrderInput!]!) {
      productPeriodOrdersUpdate(orders: $orders) {
        periodText
        ordering
      }
    }
  `);

  return (
    <Formik<FormValues>
      initialValues={{ orders: productPeriodOrders.map(({ periodText, ordering }) => ({ periodText, ordering })) }}
      enableReinitialize
      validate={({ orders }) => {
        const errors = validateProductPeriodOrders(orders);
        if (errors.every((error) => !error)) return {};
        return { orders: errors.map((error) => (error ? { ordering: error } : undefined)) };
      }}
      onSubmit={({ orders }, { setFieldError, setSubmitting }) => {
        mutate({
          variables: {
            orders: orders.map(({ periodText, ordering }) => ({ periodText, ordering: ordering as number })),
          },
          onCompleted: () => {
            toast('판매 유형 순서를 저장했어요', 'success');
            setSubmitting(false);
            refresh();
          },
          onError: (error) => {
            const message =
              parseGraphQLError(error as GraphQLError)?.[0]?.message || '순서를 저장하지 못했어요. 다시 시도해 주세요';
            setFieldError('nonFieldError', message);
            setSubmitting(false);
          },
        });
      }}
    >
      {({ values: { orders }, handleSubmit, isSubmitting }) => (
        <form onSubmit={handleSubmit}>
          <NonFieldError sx={{ marginBottom: 4 }} />
          <FormLayout>
            <Grid gapX={2} gapY={2} sx={{ alignItems: 'flex-start' }}>
              {orders.map(({ periodText }, index) => (
                <Grid.Unit key={periodText} size={[1 / 2, 1 / 5]}>
                  <NumberField
                    label={periodText}
                    name={`orders.${index}.ordering`}
                    size={'large'}
                    placeholder={'순서'}
                  />
                </Grid.Unit>
              ))}
            </Grid>
          </FormLayout>
          <View sx={{ marginTop: 4 }}>
            <Button type={'submit'} variant={'primary'} size={'large'} disabled={isSubmitting}>
              저장하기
            </Button>
          </View>
        </form>
      )}
    </Formik>
  );
};

const ProductPeriodOrder: NextPage = () => (
  <View>
    <Head siteTitle={'판매 유형 관리'} />
    <Text as={'h1'}>판매 유형 관리</Text>
    <View sx={{ marginTop: 2 }}>
      <Text sx={{ fontSize: 1, color: 'fg.muted' }}>
        숫자가 작은 유형이 앱·웹 이용권 탭에서 앞에 나와요. 같은 숫자는 쓸 수 없어요.
      </Text>
    </View>
    <View sx={{ marginTop: 5 }}>
      <ErrorBoundary>
        <Suspense fallback={<Spinner />}>
          <ProductPeriodOrderForm />
        </Suspense>
      </ErrorBoundary>
    </View>
  </View>
);

ProductPeriodOrder.getLayout = (page) => <HeaderSidebarNavPageLayout>{page}</HeaderSidebarNavPageLayout>;
ProductPeriodOrder.authenticated = true;
ProductPeriodOrder.routes = [
  {
    id: 'productPeriodOrder',
    icon: ListOrderedIcon,
    pathname: '/productPeriodOrder',
    name: '판매 유형 관리',
    permissions: ['product_update'],
  },
];

export default ProductPeriodOrder;
